"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getHealthcareApiURI = exports.getMessagingServiceURI = exports.getSessionManagerURI = exports.getPivotBackendURI = exports.webMetricsURI = exports.crmMiddlewareURI = exports.planServiceURI = exports.CDNHealthCareURI = exports.CDNPivotURI = exports.semContentStaticURI = exports.semContentURI = exports.geoLocationURI = exports.geoIpURI = exports.getValueWithoutQA = exports.getValue = exports.EnvironmentPrefix = void 0;
const utils_1 = require("@hc-frontend/utils");
/**
 * Get the prefix for environment.
 *
 * @public
 *
 * @example
 * ```ts
 * const url = geoLocationURI(EnvironmentPrefix.STG);
 * console.log(url); // https://geolocation.stg.healthcare.com
 * ```
 */
var EnvironmentPrefix;
(function (EnvironmentPrefix) {
    EnvironmentPrefix["QA"] = "qa.";
    EnvironmentPrefix["STG"] = "stg.";
    EnvironmentPrefix["PRD"] = "";
})(EnvironmentPrefix || (EnvironmentPrefix = {}));
exports.EnvironmentPrefix = EnvironmentPrefix;
/**
 * Get the prefix environment: qa./stg.
 *
 * @public
 *
 * @param {string?} prefix - A fixed prefix if this should be static
 *
 * @returns If prefix is given and if it's of type `string`, it will be returned otherwise the current url will be used for guessing the environment
 *
 * @example
 * ```ts
 * const stg = getValue(); // if `window.location.hostname` is similar to a.domain.stg.healthcare.com
 * const qa = getValue('qa.');
 * ```
 */
function getValue(prefix) {
    return typeof prefix === 'string' ? prefix : (0, utils_1.prefixEnvironment)();
}
exports.getValue = getValue;
/**
 * Get the prefix environment: stg.
 *
 * @public
 *
 * @param {string?} prefix - A fixed prefix if this should be static
 *
 * @returns If prefix is given and if it's of type `string`, it will be returned otherwise the current url will be used for guessing the environment
 * but always return `stg.` instead of `qa.`
 *
 * @example
 * ```ts
 * const stg1 = getValueWithoutQA(); // if `window.location.hostname` is similar to a.domain.qa.healthcare.com
 * const stg2 = getValueWithoutQA('stg.');
 * ```
 */
function getValueWithoutQA(prefix) {
    return typeof prefix === 'string' ? prefix : (0, utils_1.prefixEnvironmentWithoutQA)();
}
exports.getValueWithoutQA = getValueWithoutQA;
/**
 * Get the current Geo IP API endpoint to determine the current location of the user
 *
 * @public
 *
 * @returns The Geo IP API uri
 *
 * @example
 * ```ts
 * const uri = geoIpURI();
 * ```
 */
function geoIpURI() {
    return 'https://geoip.healthcare.com';
}
exports.geoIpURI = geoIpURI;
/**
 * Get the current location API endpoint to determine the current location of the user
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The location API uri
 *
 * @example
 * ```ts
 * const uri = geoLocationURI('stg.');
 * ```
 */
function geoLocationURI(prefix) {
    return `https://geolocation.${getValueWithoutQA(prefix)}healthcare.com`;
}
exports.geoLocationURI = geoLocationURI;
/**
 * Get the current SEM content API endpoint.
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The SEM content API uri
 *
 * @example
 * ```ts
 * const uri = semContentURI('qa.');
 * ```
 */
function semContentURI(prefix) {
    return `https://sem-content.${getValue(prefix)}healthcare.com`;
}
exports.semContentURI = semContentURI;
/**
 * Get the current SEM content static API endpoint.
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The SEM content static API uri
 *
 * @example
 * ```ts
 * const uri = semContentStaticURI('qa.');
 * ```
 */
function semContentStaticURI(prefix) {
    return `https://sem-content.static.${getValue(prefix)}healthcare.com`;
}
exports.semContentStaticURI = semContentStaticURI;
/**
 * Get the current CDN endpoint to fetch static assets for **PivotHealth**
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The CDN uri
 *
 * @example
 * ```ts
 * const uri = CDNPivotURI('qa.');
 * ```
 */
function CDNPivotURI(prefix) {
    return `https://cdn.${getValue(prefix)}pivothealth.com`;
}
exports.CDNPivotURI = CDNPivotURI;
/**
 * Get the current CDN endpoint to fetch static assets for **Healthcare.com**
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The CDN uri
 *
 * @example
 * ```ts
 * const uri = CDNHealthCareURI('qa.');
 * ```
 */
function CDNHealthCareURI(prefix) {
    return `https://cdn.${getValue(prefix)}healthcare.com`;
}
exports.CDNHealthCareURI = CDNHealthCareURI;
/**
 * Get the current plan service API endpoint to fetch plans for the user
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The plan service API endpoint uri
 *
 * @example
 * ```ts
 * const uri = planServiceURI('qa.');
 * ```
 */
function planServiceURI(prefix) {
    return `https://plans-srv.${getValue(prefix)}healthcare.com`;
}
exports.planServiceURI = planServiceURI;
/**
 * Get the current CRM middleware API endpoint uri
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The CRM middleware API endpoint uri
 *
 * @example
 * ```ts
 * const uri = crmMiddlewareURI('qa.');
 * ```
 */
function crmMiddlewareURI(prefix) {
    return `https://crm-middleware.${getValue(prefix)}pivothealth.com`;
}
exports.crmMiddlewareURI = crmMiddlewareURI;
/**
 * Get the current Web Metrics API endpoint uri
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The Web Metrics API endpoint uri
 *
 * @example
 * ```ts
 * const uri = crmMiddlewareURI('qa.');
 * ```
 */
function webMetricsURI(prefix) {
    return `https://web-metrics.${getValue(prefix)}healthcare.com`;
}
exports.webMetricsURI = webMetricsURI;
/**
 * Get the current Backend API endpoint uri
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The backend endpoint URI
 *
 * @example
 * ```ts
 * const uri = getPivotBackendURI('qa.');
 * ```
 */
function getPivotBackendURI(prefix) {
    return `https://backend.${getValue(prefix)}pivothealth.com`;
}
exports.getPivotBackendURI = getPivotBackendURI;
/**
 * Get the current Backend API endpoint uri
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The backend endpoint URI
 *
 * @example
 * ```ts
 * const uri = getSessionManagerURI('qa.');
 * ```
 */
function getSessionManagerURI(prefix) {
    return `https://session-manager-srv.${getValueWithoutQA(prefix)}healthcare.com`;
}
exports.getSessionManagerURI = getSessionManagerURI;
/**
 * Get the messaging service API endpoint uri
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns The messaging service URI
 *
 * @example
 * ```ts
 * const uri = getMessagingServiceURI('qa.');
 * ```
 */
function getMessagingServiceURI(prefix) {
    return `https://messaging-service.${getValue(prefix)}pivothealth.com`;
}
exports.getMessagingServiceURI = getMessagingServiceURI;
/**
 * Get the Healthcare API endpoint uri
 *
 * @public
 *
 * @param {string?} prefix - An environment to append to this url. If not provided then `window.location.hostname` is used to determine the environment
 *
 * @returns Healthcare api URI
 *
 * @example
 * ```ts
 * const uri = getHealthcareApiURI('qa.');
 * ```
 */
function getHealthcareApiURI(prefix) {
    return `https://api.${getValue(prefix)}healthcare.com`;
}
exports.getHealthcareApiURI = getHealthcareApiURI;
//# sourceMappingURL=endpoints.js.map